import {
  Button,
  Collapse,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Radio,
  RadioGroup,
  Stack,
} from '@cardboard-ui/react';
import { t } from '@lingui/macro';
import { OverlayProps } from 'components/TaskOverlay';
import React, { useState } from 'react';
import { graphql, useMutation } from 'utils/graphClient';
import { notEmpty } from 'utils/NotEmptyFilter';
import { PermissionRuleConfig } from '.';
import { SpaceMemberSelector } from '../MemberSelector/SpaceMemberSelector';
import {
  Permission,
  PermissionRuleUpdateModal_updateNodePermissions_Mutation,
  PermissionRuleUpdateModal_updateNodePermissions_Mutation$variables,
} from './__generated__/PermissionRuleUpdateModal_updateNodePermissions_Mutation.graphql';

const updatePermissionsMutation = graphql`
  mutation PermissionRuleUpdateModal_updateNodePermissions_Mutation(
    $input: PermissionsUpdatePermissionsMutationInput!
  ) {
    updateNodePermissions(input: $input) {
      node {
        ...SpaceEventsModulePermissionRules_module
        ...SpaceAnnouncementsModulePermissionRules_module
        ...SpaceFilesModulePermissionRules_module
        ...PermissionRules_node
      }
    }
  }
`;

export type { Permission };
export interface PermissionRuleUpdateModalProps extends OverlayProps {
  spaceId: string;
  config: PermissionRuleConfig;
  graphRuleName: Permission;
  nodeId: string;
}

function hasSameMemberIds(array1: string[], array2: string[]): boolean {
  if (array1.length !== array2.length) {
    return false;
  }
  const sortedArray2 = array2.sort();
  return array1.sort().every((v, i) => sortedArray2[i] === v);
}

type PermissionLevel = 'owners-only' | 'allow-all' | 'specific-members';

function permissionRuleConfigToPermissionLevel(
  config: PermissionRuleConfig,
): PermissionLevel {
  if (!config.allowAllMembers && config.allowedMembers.length === 0) {
    return 'owners-only';
  }
  if (config.allowAllMembers) {
    return 'allow-all';
  }
  return 'specific-members';
}

export const PermissionRuleUpdateModal = ({
  config,
  nodeId,
  graphRuleName,
  spaceId,
  ...props
}: PermissionRuleUpdateModalProps) => {
  const [updatePermissions, isUpdating] =
    useMutation<PermissionRuleUpdateModal_updateNodePermissions_Mutation>(
      updatePermissionsMutation,
    );

  const [newConfig, setNewConfig] = useState<PermissionRuleConfig>(config);
  const [showMemberPicker, setShowMemberPicker] = useState(
    !newConfig.allowAllMembers && newConfig.allowedMembers.length > 0,
  );

  const onSave = () => {
    let variables: PermissionRuleUpdateModal_updateNodePermissions_Mutation$variables =
      {
        input: {
          nodeId,
          rule: graphRuleName,
        },
      };
    switch (permissionRuleConfigToPermissionLevel(newConfig)) {
      case 'owners-only':
        variables.input.revokeAllMembers = true;
        variables.input.revokeRoles = ['SPACE_MEMBERS'];
        break;
      case 'allow-all':
        variables.input.revokeAllMembers = true;
        variables.input.allowRoles = ['SPACE_MEMBERS'];
        break;
      case 'specific-members':
        variables.input.revokeRoles = ['SPACE_MEMBERS'];
        variables.input.allowMemberIds = newConfig.allowedMembers
          .map(({ id }) => id)
          .filter(notEmpty);
        variables.input.revokeMemberIds = config.allowedMembers
          .filter(
            (previouslyAddedMember) =>
              !newConfig.allowedMembers.find(
                ({ id }) => id === previouslyAddedMember.id,
              ),
          )
          .map(({ id }) => id)
          .filter(notEmpty);
    }

    updatePermissions({ variables, onCompleted: props.onClose });
  };

  const canSave =
    newConfig.allowAllMembers !== config.allowAllMembers ||
    !hasSameMemberIds(
      newConfig.allowedMembers.map(({ id }) => id).filter(notEmpty),
      config.allowedMembers.map(({ id }) => id).filter(notEmpty),
    );

  return (
    <Modal {...props}>
      <ModalOverlay />
      <ModalContent role="alertdialog">
        <ModalHeader>{t`Update permissions`}</ModalHeader>
        <ModalBody>
          <Stack>
            <Stack>
              <RadioGroup
                defaultValue={permissionRuleConfigToPermissionLevel(newConfig)}
              >
                <Stack>
                  <Radio
                    value="owners-only"
                    onChange={({ currentTarget }) => {
                      if (currentTarget.checked) {
                        setNewConfig({
                          allowAllMembers: false,
                          allowedMembers: [],
                        });
                        setShowMemberPicker(false);
                      }
                    }}
                  >{t`Owners only`}</Radio>
                  <Radio
                    value="allow-all"
                    onChange={({ currentTarget }) => {
                      if (currentTarget.checked) {
                        setNewConfig({
                          allowAllMembers: true,
                          allowedMembers: [],
                        });
                        setShowMemberPicker(false);
                      }
                    }}
                  >{t`Allow all members`}</Radio>
                  <Radio
                    value="specific-members"
                    onChange={({ currentTarget }) => {
                      if (currentTarget.checked) {
                        setNewConfig({
                          allowAllMembers: false,
                          allowedMembers: [],
                        });
                        setShowMemberPicker(true);
                      }
                    }}
                  >{t`Allow specific members`}</Radio>
                </Stack>
              </RadioGroup>

              <Collapse
                in={showMemberPicker}
                style={{ overflow: showMemberPicker ? 'unset' : 'hidden' }}
              >
                <SpaceMemberSelector
                  spaceId={spaceId}
                  selectedMemberIds={config.allowedMembers
                    .map(({ id }) => id)
                    .filter(notEmpty)}
                  selectedEmails={[]}
                  onSelectedMembersUpdate={(_, newSelectedMembers) =>
                    setNewConfig({
                      allowAllMembers: false,
                      allowedMembers: newSelectedMembers,
                    })
                  }
                  disableEmailInvite
                  onSelectedEmailsUpdate={() => {}}
                  allowSelectAll={false}
                />
              </Collapse>
            </Stack>
          </Stack>
        </ModalBody>
        <ModalFooter alignItems="end">
          <Button
            onClick={onSave}
            isDisabled={!canSave}
            colorScheme="blue"
            isLoading={isUpdating}
          >{t`Save`}</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
